import { apiFetch } from '../apiFetch'

export type ReplenishPolicy = 'MANUAL' | 'MIN_MAX' | 'REORDER_POINT'

export interface StockPolicyInventoryDto {
  physical_available: number
  reserved_base: number
  available: number
  incoming_base: number
  incoming_unresolved: boolean
  quarantine: number
  blocked: number
  damaged: number
  rework: number
  threshold: number
  below_threshold: boolean
  out_of_stock: boolean
}

export interface RecentPurchaseItem {
  po_id: string
  po_no: string
  supplier_id: string
  supplier_name: string
  unit_price_actual: number
  price_uom: string
  order_qty: number
  order_uom: string
  created_at: string
}

export interface MaterialStockPolicyDetailDto {
  material_id: string
  material_code: string
  material_name: string
  spec: string | null
  uom_default: string | null
  kind: string
  status: string
  policy: {
    min_qty: number
    max_qty: number | null
    reorder_point: number
    safety_stock: number
    replenish_policy: ReplenishPolicy
    preferred_supplier_id: string | null
    preferred_supplier_name: string | null
    lead_time_days: number | null
    created_at: string | null
    updated_at: string | null
    updated_by: string | null
  }
  inventory: StockPolicyInventoryDto
  recent_purchases: RecentPurchaseItem[]
}

export interface UpdateStockPolicyPayload {
  min_qty?: number | null
  max_qty?: number | null
  reorder_point?: number | null
  safety_stock?: number | null
  replenish_policy?: ReplenishPolicy
  preferred_supplier_id?: string | null
  lead_time_days?: number | null
}

export type StockPolicyApiError = {
  ok: false
  error: { code: string; message: string; details?: Record<string, unknown> }
}

export async function getMaterialStockPolicyApi(
  materialId: string,
): Promise<MaterialStockPolicyDetailDto> {
  const res = await apiFetch(`/api/materials/${encodeURIComponent(materialId)}/stock-policy`)
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: MaterialStockPolicyDetailDto }
    | StockPolicyApiError
    | null

  if (!res.ok || !body || !body.ok) {
    const msg = (body as StockPolicyApiError)?.error?.message || 'Không thể tải chính sách tồn kho của vật tư'
    throw new Error(msg)
  }

  return body.data
}

export async function updateMaterialStockPolicyApi(
  materialId: string,
  payload: UpdateStockPolicyPayload,
): Promise<void> {
  const res = await apiFetch(`/api/materials/${encodeURIComponent(materialId)}/stock-policy`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: unknown }
    | StockPolicyApiError
    | null

  if (!res.ok || !body || !body.ok) {
    const msg = (body as StockPolicyApiError)?.error?.message || 'Không thể lưu chính sách tồn kho'
    throw new Error(msg)
  }
}

export async function getMaterialRecentPurchasesApi(
  materialId: string,
): Promise<RecentPurchaseItem[]> {
  const res = await apiFetch(`/api/materials/${encodeURIComponent(materialId)}/last-purchase-price`)
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: RecentPurchaseItem[] }
    | StockPolicyApiError
    | null

  if (!res.ok || !body || !body.ok) {
    const msg = (body as StockPolicyApiError)?.error?.message || 'Không thể tải giá mua gần nhất'
    throw new Error(msg)
  }

  return body.data
}
